import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, Plus, Trash2, CheckCircle2, AlertCircle, Phone, Mail } from "lucide-react";
import { GRADE_OPTIONS } from "@/lib/athleteTaxonomy";
import SportsPicker from "@/components/SportsPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyAthlete = { first_name: "", last_name: "", dob: "", grade: "", sports: [], sports_other: "", email: "" };

// Map a Pydantic error `loc` array (e.g. ["body","athletes",0,"first_name"])
// to the same field-key we use in client-side validation, so backend 422s
// can also scroll to the right input.
function locToFieldKey(loc) {
    if (!Array.isArray(loc)) return null;
    if (loc.includes("athletes")) {
        const idx = loc[loc.indexOf("athletes") + 1];
        const fld = loc[loc.length - 1];
        return `ath-${idx}-${fld}`;
    }
    const fld = loc[loc.length - 1];
    if (["email", "phone", "parent_first_name", "parent_last_name", "preferred_time", "preferred_days", "referral_source", "referral_details"].includes(fld)) {
        return fld;
    }
    return null;
}

export default function AssessmentRequestModal({ open, onClose }) {
    const [parent, setParent] = useState({
        parent_first_name: "",
        parent_last_name: "",
        email: "",
        phone: "",
    });
    const [signupFor, setSignupFor] = useState("children");
    const [athletes, setAthletes] = useState([{ ...emptyAthlete }]);
    const [timeWindows, setTimeWindows] = useState([]);
    const [preferredDays, setPreferredDays] = useState([]);
    const [notes, setNotes] = useState("");
    const [goals, setGoals] = useState("");
    // Attribution: "How did you find us?" — required so we know which
    // marketing channels are working. If they pick Referral, we ask for
    // a follow-up free-text "from whom?"
    const [referralSource, setReferralSource] = useState("");
    const [referralDetails, setReferralDetails] = useState("");
    const [company, setCompany] = useState(""); // honeypot — humans never touch this
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    // Inline error: tracks which specific field failed validation so we can
    // highlight it, scroll to it, and focus it. Cleared as soon as the user
    // touches the offending input.
    const [fieldError, setFieldError] = useState(null); // {field, message}
    const [submitError, setSubmitError] = useState(null); // sticky banner
    const [retrying, setRetrying] = useState(0); // 0 = first try, 1+ = retry attempt #
    // Stamp the moment the modal opens so the backend can drop sub-2s submissions.
    const [formOpenedAt] = useState(() => Date.now());

    // Single ref-map keyed by our field key (e.g. "email", "ath-0-first_name").
    // Each input registers itself via the `setRef` helper below.
    const fieldRefs = useRef({});
    const setFieldRef = (key) => (el) => {
        if (el) fieldRefs.current[key] = el;
        else delete fieldRefs.current[key];
    };

    // When fieldError changes, scroll the offending field into view + focus it.
    useEffect(() => {
        if (!fieldError) return;
        const el = fieldRefs.current[fieldError.field];
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Slight delay so smooth-scroll doesn't fight with focus jump.
        setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (_e) { /* noop */ } }, 250);
    }, [fieldError]);

    // Clear error if user starts editing the offending field.
    const clearErrorFor = (field) => {
        if (fieldError && fieldError.field === field) setFieldError(null);
    };

    // Abandoned-booking recovery — debounced draft tracking. If the parent
    // types an email + first name then walks away, /api/assessment-requests/draft
    // upserts a row. Cron in routes/automations.py fires a "finish booking"
    // email after 30 min of inactivity. Idempotent and silent — never blocks
    // the form. Cleared on successful submit (the doc lookup matches the
    // assessment_requests email so the cron skips it automatically).
    useEffect(() => {
        if (!open) return;
        const email = (parent.email || "").trim().toLowerCase();
        const first = (parent.parent_first_name || "").trim();
        // Only ping once we have both — avoids noise on every keystroke.
        if (!email || !first || !/.@./.test(email)) return;
        const handle = setTimeout(() => {
            axios.post(
                `${API}/assessment-requests/draft`,
                {
                    email,
                    parent_first_name: first,
                    parent_last_name: (parent.parent_last_name || "").trim(),
                    phone: (parent.phone || "").trim(),
                    signup_for: signupFor || "",
                    page: "modal",
                    user_agent: navigator.userAgent?.slice(0, 200) || "",
                },
                { timeout: 8000 },
            ).catch(() => { /* tracking is best-effort */ });
        }, 4000);
        return () => clearTimeout(handle);
    }, [open, parent.email, parent.parent_first_name, parent.parent_last_name, parent.phone, signupFor]);

    if (!open) return null;

    const close = () => {
        if (submitting) return;
        setParent({ parent_first_name: "", parent_last_name: "", email: "", phone: "" });
        setSignupFor("children");
        setAthletes([{ ...emptyAthlete }]);
        setTimeWindows([]);
        setPreferredDays([]);
        setNotes("");
        setSubmitError(null);
        setDone(false);
        onClose();
    };

    const addAthlete = () => setAthletes((a) => [...a, { ...emptyAthlete }]);
    const removeAthlete = (i) => setAthletes((a) => a.filter((_, idx) => idx !== i));
    const updateAthlete = (i, patch) =>
        setAthletes((a) => a.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

    const validate = () => {
        if (!parent.parent_first_name.trim()) {
            return { field: "parent_first_name", message: "Add the parent / guardian first name." };
        }
        if (!parent.parent_last_name.trim()) {
            return { field: "parent_last_name", message: "Add the parent / guardian last name." };
        }
        if (!parent.email.trim()) {
            return { field: "email", message: "Email is required." };
        }
        // Light client-side format check — must be like x@y.z. The backend uses
        // Pydantic EmailStr which rejects domains without a TLD, so we surface
        // that here in plain English.
        const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!EMAIL_RE.test(parent.email.trim())) {
            return { field: "email", message: "That email looks off — include the full address (e.g., name@example.com)." };
        }
        if (!parent.phone.trim()) {
            return { field: "phone", message: "Phone is required." };
        }
        if (timeWindows.length === 0) {
            return { field: "preferred_time", message: "Pick at least one time window." };
        }
        if (preferredDays.length === 0) {
            return { field: "preferred_days", message: "Pick at least one day that works." };
        }
        if (!referralSource) {
            return { field: "referral_source", message: "Let us know how you found us." };
        }
        if (referralSource === "referral" && !referralDetails.trim()) {
            return { field: "referral_details", message: "Mind sharing who referred you?" };
        }
        if (signupFor !== "self") {
            if (!athletes.length) {
                return { field: "ath-0-first_name", message: "Add at least one athlete." };
            }
            for (let i = 0; i < athletes.length; i++) {
                const a = athletes[i];
                if (!a.first_name.trim()) {
                    return { field: `ath-${i}-first_name`, message: `Child ${i + 1} needs a first name.` };
                }
                if (!a.last_name.trim()) {
                    return { field: `ath-${i}-last_name`, message: `Child ${i + 1} needs a last name.` };
                }
                if (a.email && a.email.trim() && !EMAIL_RE.test(a.email.trim())) {
                    return { field: `ath-${i}-email`, message: `Child ${i + 1}'s email looks off — leave it blank or use the full address.` };
                }
            }
        }
        return null;
    };

    const submit = async () => {
        const err = validate();
        if (err) {
            setFieldError(err);
            setSubmitError(err.message);
            toast.error(err.message);
            return;
        }
        setFieldError(null);
        setSubmitError(null);
        setSubmitting(true);
        try {
            const athletesPayload = signupFor === "self"
                ? [{
                    first_name: parent.parent_first_name.trim(),
                    last_name: parent.parent_last_name.trim(),
                    dob: null,
                    email: parent.email.trim().toLowerCase() || null,
                }]
                : athletes.map((a) => ({
                    first_name: a.first_name.trim(),
                    last_name: a.last_name.trim(),
                    dob: a.dob || null,
                    grade: a.grade || null,
                    sports: a.sports || [],
                    sports_other: (a.sports_other || "").trim() || null,
                    email: (a.email || "").trim().toLowerCase() || null,
                }));
            // Auto-retry on transient network/server failure. We retry 3 times
            // with exponential backoff (0.5s, 1.5s, 3.5s) before surfacing an
            // error to the parent. Only retry on network errors or 5xx — never
            // on 4xx (those are validation errors the parent must see).
            const requestBody = {
                ...parent,
                signup_for: signupFor,
                athletes: athletesPayload,
                preferred_time: timeWindows.join(", "),
                sign_waiver_now: false,
                waiver_signed_name: null,
                notes: notes.trim() || "",
                goals: goals.trim() || "",
                referral_source: referralSource || null,
                referral_details: referralSource === "referral" ? referralDetails.trim() : null,
                redirect_url: `${window.location.origin}/auth/magic`,
                accept_url_base: window.location.origin,
                company: company,
                form_opened_at: formOpenedAt,
            };
            const BACKOFFS_MS = [0, 500, 1500, 3500]; // 4 attempts, ~5.5s total
            let attempt = 0;
            let lastErr = null;
            for (attempt = 0; attempt < BACKOFFS_MS.length; attempt += 1) {
                if (BACKOFFS_MS[attempt] > 0) {
                    setRetrying(attempt);
                    await new Promise((r) => setTimeout(r, BACKOFFS_MS[attempt]));
                }
                try {
                    await axios.post(`${API}/assessment-requests`, requestBody, { timeout: 15000 });
                    lastErr = null;
                    break;
                } catch (err) {
                    lastErr = err;
                    const status = err?.response?.status;
                    // 4xx (validation) errors are NEVER retryable. Network or 5xx errors are.
                    if (status && status < 500 && status !== 408 && status !== 429) {
                        throw err;
                    }
                    // else: retry
                }
            }
            setRetrying(0);
            if (lastErr) {
                throw lastErr; // exhausted retries
            }
            setDone(true);
        } catch (e) {
            // Pydantic 422 returns `detail` as a list of {loc, msg}. If any of
            // those map to a known field, scroll/highlight it. Otherwise show
            // a clean description.
            const detail = e?.response?.data?.detail;
            let description;
            if (Array.isArray(detail)) {
                description = detail
                    .map((d) => {
                        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "form";
                        return `${field}: ${d.msg || d.message || "invalid"}`;
                    })
                    .join("\n");
                // Highlight the first known offending field
                for (const d of detail) {
                    const key = locToFieldKey(d.loc);
                    if (key && fieldRefs.current[key]) {
                        setFieldError({ field: key, message: d.msg || "Please fix this field." });
                        break;
                    }
                }
            } else if (typeof detail === "string") {
                description = detail;
            } else if (e?.message) {
                description = e.message;
            }
            // Persistent sticky banner so the parent CANNOT miss it. Toasts
            // disappear in ~5s and have been the silent-failure culprit.
            setSubmitError(
                description || "We couldn't submit your form. Please check the highlighted field and try again — your info is still here."
            );
            toast.error("Couldn't submit — please fix the highlighted field", { description });
        } finally {
            setSubmitting(false);
            setRetrying(0);
        }
    };

    if (done) {
        return (
            <Shell onClose={close}>
                <div data-testid="assessment-request-success" className="text-center py-10">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-navy-900/60 ring-1 ring-brand-navy-700 mb-5">
                        <CheckCircle2 className="w-8 h-8 text-brand-navy-300" />
                    </div>
                    <h3 className="font-display text-2xl tracking-wider text-white mb-2">
                        REQUEST IN.
                    </h3>
                    <p className="text-zinc-200 text-sm leading-relaxed max-w-md mx-auto">
                        Coach will reach out shortly to confirm a time. Check your inbox — we sent
                        you a sign-in link plus our <strong className="text-white">participation waiver</strong> pre-filled with what you just shared.
                    </p>
                    <button type="button" onClick={close} className="btn-tactical mt-8" data-testid="assessment-request-done">Done</button>
                </div>
            </Shell>
        );
    }

    return (
        <Shell onClose={close}>
            <div data-testid="assessment-request-modal" className="space-y-4">
                {/* Honeypot — humans never see / touch this. Bots fill every input
                    and the backend silently drops the submission. Off-screen via
                    inline style so adblock / dark themes can't accidentally reveal it. */}
                <div
                    aria-hidden="true"
                    style={{
                        position: "absolute",
                        left: "-10000px",
                        top: "auto",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                    }}
                >
                    <label htmlFor="company-website">Company website (leave blank)</label>
                    <input
                        id="company-website"
                        type="text"
                        name="company"
                        tabIndex={-1}
                        autoComplete="off"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                    />
                </div>

                <div>
                    <h3 className="font-display text-2xl tracking-wider text-white">
                        HAVE QUESTIONS? BOOK A CONSULT!
                    </h3>
                    <p className="text-zinc-200 text-xs mt-1 leading-relaxed">
                        <em className="text-zinc-300">"The hardest worker on the team, but it's not showing up on the field."</em> Sound
                        familiar? Most athletes are more athletic than they're showing — they just need the
                        right program. The assessment finds what's hiding it, in numbers. 45 minutes. You
                        watch. You get the data.
                    </p>
                    <p className="text-zinc-400 text-xs mt-2">
                        Tell us a bit about you, the athlete, and a time that works. Coach will follow up to confirm.
                    </p>
                </div>

                <div className="grid grid-cols-3 gap-2">
                    {[
                        { v: "self", l: "Myself" },
                        { v: "children", l: "My child" },
                        { v: "both", l: "Me + child" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            data-testid={`signup-for-${opt.v}`}
                            onClick={() => setSignupFor(opt.v)}
                            className={`py-3 border text-[11px] tracking-[0.2em] uppercase transition ${
                                signupFor === opt.v
                                    ? "border-brand-red text-brand-red bg-brand-red/10"
                                    : "border-zinc-800 text-zinc-300 hover:border-zinc-600"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>

                <div className="border border-zinc-800 rounded-lg p-4 space-y-3">
                    <div className="text-[11px] tracking-[0.25em] uppercase text-brand-navy-300">
                        {signupFor === "self" ? "Your info" : "Parent / Guardian info"}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="First name *" error={fieldError?.field === "parent_first_name" ? fieldError.message : null}>
                            <Input
                                inputRef={setFieldRef("parent_first_name")}
                                value={parent.parent_first_name}
                                onChange={(v) => { setParent({ ...parent, parent_first_name: v }); clearErrorFor("parent_first_name"); }}
                                onFocus={() => clearErrorFor("parent_first_name")}
                                hasError={fieldError?.field === "parent_first_name"}
                                testid="req-first-name"
                            />
                        </Field>
                        <Field label="Last name *" error={fieldError?.field === "parent_last_name" ? fieldError.message : null}>
                            <Input
                                inputRef={setFieldRef("parent_last_name")}
                                value={parent.parent_last_name}
                                onChange={(v) => { setParent({ ...parent, parent_last_name: v }); clearErrorFor("parent_last_name"); }}
                                onFocus={() => clearErrorFor("parent_last_name")}
                                hasError={fieldError?.field === "parent_last_name"}
                                testid="req-last-name"
                            />
                        </Field>
                        <Field label="Email *" error={fieldError?.field === "email" ? fieldError.message : null}>
                            <Input
                                inputRef={setFieldRef("email")}
                                type="email"
                                value={parent.email}
                                onChange={(v) => { setParent({ ...parent, email: v }); clearErrorFor("email"); }}
                                onFocus={() => clearErrorFor("email")}
                                hasError={fieldError?.field === "email"}
                                testid="req-email"
                            />
                        </Field>
                        <Field label="Phone *" error={fieldError?.field === "phone" ? fieldError.message : null}>
                            <Input
                                inputRef={setFieldRef("phone")}
                                value={parent.phone}
                                onChange={(v) => { setParent({ ...parent, phone: v }); clearErrorFor("phone"); }}
                                onFocus={() => clearErrorFor("phone")}
                                hasError={fieldError?.field === "phone"}
                                testid="req-phone"
                                placeholder="(555) 555-5555"
                            />
                        </Field>
                    </div>
                </div>

                {signupFor !== "self" && (
                    <div className="border border-zinc-800 rounded-lg p-4 space-y-3">
                        <div className="text-[11px] tracking-[0.25em] uppercase text-brand-red">
                            {signupFor === "both" ? "Child info" : "Child / Athlete info"}
                        </div>
                        {athletes.map((a, i) => (
                            <div key={i} className="border border-zinc-900 bg-black/40 rounded-lg p-3 space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-zinc-300">Child {i + 1}</span>
                                    {athletes.length > 1 && (
                                        <button type="button" onClick={() => removeAthlete(i)} className="text-zinc-300 hover:text-brand-red"><Trash2 className="w-4 h-4" /></button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="First name *" error={fieldError?.field === `ath-${i}-first_name` ? fieldError.message : null}>
                                        <Input
                                            inputRef={setFieldRef(`ath-${i}-first_name`)}
                                            value={a.first_name}
                                            onChange={(v) => { updateAthlete(i, { first_name: v }); clearErrorFor(`ath-${i}-first_name`); }}
                                            onFocus={() => clearErrorFor(`ath-${i}-first_name`)}
                                            hasError={fieldError?.field === `ath-${i}-first_name`}
                                            testid={`req-ath-first-${i}`}
                                        />
                                    </Field>
                                    <Field label="Last name *" error={fieldError?.field === `ath-${i}-last_name` ? fieldError.message : null}>
                                        <Input
                                            inputRef={setFieldRef(`ath-${i}-last_name`)}
                                            value={a.last_name}
                                            onChange={(v) => { updateAthlete(i, { last_name: v }); clearErrorFor(`ath-${i}-last_name`); }}
                                            onFocus={() => clearErrorFor(`ath-${i}-last_name`)}
                                            hasError={fieldError?.field === `ath-${i}-last_name`}
                                            testid={`req-ath-last-${i}`}
                                        />
                                    </Field>
                                    <Field label="Date of birth">
                                        <Input type="date" value={a.dob} onChange={(v) => updateAthlete(i, { dob: v })} testid={`req-ath-dob-${i}`} />
                                    </Field>
                                    <Field label="Grade">
                                        <select
                                            data-testid={`req-ath-grade-${i}`}
                                            value={a.grade || ""}
                                            onChange={(e) => updateAthlete(i, { grade: e.target.value })}
                                            className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                                        >
                                            <option value="">Select grade…</option>
                                            {GRADE_OPTIONS.map((g) => (
                                                <option key={g.value} value={g.value}>{g.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Child email (optional)" error={fieldError?.field === `ath-${i}-email` ? fieldError.message : null}>
                                        <Input
                                            inputRef={setFieldRef(`ath-${i}-email`)}
                                            type="email"
                                            value={a.email}
                                            onChange={(v) => { updateAthlete(i, { email: v }); clearErrorFor(`ath-${i}-email`); }}
                                            onFocus={() => clearErrorFor(`ath-${i}-email`)}
                                            hasError={fieldError?.field === `ath-${i}-email`}
                                            testid={`req-ath-email-${i}`}
                                        />
                                    </Field>
                                </div>
                                <div>
                                    <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-2">Sport(s) — pick all that apply</div>
                                    <SportsPicker
                                        value={a.sports || []}
                                        other={a.sports_other || ""}
                                        onChange={(sports, sports_other) => updateAthlete(i, { sports, sports_other })}
                                        testidPrefix={`req-ath-sports-${i}`}
                                    />
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={addAthlete} className="btn-ghost-tactical inline-flex" data-testid="add-athlete">
                            <Plus className="w-4 h-4" /> Add another child
                        </button>
                    </div>
                )}

                <Field label="Preferred days *" error={fieldError?.field === "preferred_days" ? fieldError.message : null}>
                    <div
                        ref={setFieldRef("preferred_days")}
                        tabIndex={-1}
                        className={`grid grid-cols-7 gap-1.5 ${fieldError?.field === "preferred_days" ? "ring-1 ring-brand-red/50 rounded-lg p-1" : ""}`}
                    >
                        {[
                            { v: "Mon", l: "M" },
                            { v: "Tue", l: "T" },
                            { v: "Wed", l: "W" },
                            { v: "Thu", l: "T" },
                            { v: "Fri", l: "F" },
                            { v: "Sat", l: "S" },
                            { v: "Sun", l: "S" },
                        ].map((opt) => {
                            const selected = preferredDays.includes(opt.v);
                            return (
                                <button
                                    key={opt.v}
                                    type="button"
                                    data-testid={`pref-day-${opt.v.toLowerCase()}`}
                                    onClick={() => {
                                        setPreferredDays((prev) => selected ? prev.filter((x) => x !== opt.v) : [...prev, opt.v]);
                                        clearErrorFor("preferred_days");
                                    }}
                                    className={`py-2.5 px-1 border text-center transition ${
                                        selected
                                            ? "border-brand-red text-brand-red bg-brand-red/10"
                                            : "border-zinc-800 text-zinc-200 hover:border-zinc-600"
                                    }`}
                                >
                                    <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-400">{opt.v}</div>
                                    <div className="text-base font-semibold">{opt.l}</div>
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-zinc-300 mt-2">Tap any day(s) that work — coach will confirm one of them.</p>
                </Field>

                <Field label="Preferred time *" error={fieldError?.field === "preferred_time" ? fieldError.message : null}>
                    <div
                        ref={setFieldRef("preferred_time")}
                        tabIndex={-1}
                        className={`grid grid-cols-3 gap-2 ${fieldError?.field === "preferred_time" ? "ring-1 ring-brand-red/50 rounded-lg p-1" : ""}`}
                    >
                        {[
                            { v: "Morning (6am – 10am)", l: "Morning", sub: "6 – 10 AM" },
                            { v: "Midday (10am – 2pm)", l: "Midday", sub: "10 AM – 2 PM" },
                            { v: "Afternoon / Evening (3pm – 7pm)", l: "Afternoon / Eve", sub: "3 – 7 PM" },
                        ].map((opt) => {
                            const selected = timeWindows.includes(opt.v);
                            return (
                                <button
                                    key={opt.v}
                                    type="button"
                                    data-testid={`time-window-${opt.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    onClick={() => {
                                        setTimeWindows((prev) => selected ? prev.filter((x) => x !== opt.v) : [...prev, opt.v]);
                                        clearErrorFor("preferred_time");
                                    }}
                                    className={`py-3 px-2 border text-center transition ${
                                        selected
                                            ? "border-brand-red text-brand-red bg-brand-red/10"
                                            : "border-zinc-800 text-zinc-200 hover:border-zinc-600"
                                    }`}
                                >
                                    <div className="text-[12px] font-semibold tracking-wider uppercase">{opt.l}</div>
                                    <div className="text-[10px] text-zinc-300 mt-0.5">{opt.sub}</div>
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-zinc-300 mt-2">Pick one or more — coach will confirm a specific time.</p>
                </Field>

                <Field
                    label="How did you find us? *"
                    error={fieldError?.field === "referral_source" ? fieldError.message : null}
                >
                    <div
                        ref={setFieldRef("referral_source")}
                        className={`grid sm:grid-cols-3 gap-2 ${fieldError?.field === "referral_source" ? "ring-1 ring-brand-red/50 rounded-lg p-1" : ""}`}
                    >
                        {[
                            { value: "google", label: "Google Search" },
                            { value: "referral", label: "Referral" },
                            { value: "other", label: "Other" },
                        ].map((opt) => {
                            const active = referralSource === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    data-testid={`req-referral-${opt.value}`}
                                    onClick={() => {
                                        setReferralSource(opt.value);
                                        if (opt.value !== "referral") setReferralDetails("");
                                        clearErrorFor("referral_source");
                                    }}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${active
                                        ? "bg-brand-red border-brand-red text-white"
                                        : "bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-brand-red/50"}`}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    {referralSource === "referral" && (
                        <div className="mt-3">
                            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 block mb-1.5">
                                Who referred you? *
                            </label>
                            <input
                                type="text"
                                ref={setFieldRef("referral_details")}
                                value={referralDetails}
                                onChange={(e) => {
                                    setReferralDetails(e.target.value);
                                    clearErrorFor("referral_details");
                                }}
                                placeholder="A friend's name, a coach, a team, anywhere…"
                                data-testid="req-referral-details"
                                className={`w-full bg-zinc-900 border ${fieldError?.field === "referral_details" ? "border-brand-red/70" : "border-zinc-700"} rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red`}
                            />
                            {fieldError?.field === "referral_details" && (
                                <p className="text-[11px] text-brand-red mt-1">{fieldError.message}</p>
                            )}
                        </div>
                    )}
                </Field>

                <Field label="Main goals — what would feel like a win?">
                    <textarea
                        rows={2}
                        value={goals}
                        onChange={(e) => setGoals(e.target.value)}
                        data-testid="req-goals"
                        placeholder="e.g. Get faster for soccer tryouts, build strength for swimming, stay healthy through the season…"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                    />
                </Field>

                <Field label="Anything else? (optional)">
                    <textarea
                        rows={2}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid="req-notes"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                    />
                </Field>

                {submitError && (
                    <div
                        data-testid="assessment-submit-error"
                        role="alert"
                        className="flex items-start gap-3 border border-red-700 bg-red-950/40 text-red-100 rounded-lg px-4 py-3 text-sm"
                    >
                        <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        <div className="flex-1">
                            <div className="font-bold mb-0.5">Submission failed</div>
                            <div className="text-red-200 whitespace-pre-line leading-snug">{submitError}</div>
                            <div className="text-[11px] text-red-300/70 mt-1.5">Your info is still here. Fix the issue above and tap Request assessment again. If this keeps happening, call us at (425) 641-4310.</div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={close} className="btn-ghost-tactical" disabled={submitting}>Cancel</button>
                    <button
                        type="button"
                        data-testid="req-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="btn-tactical"
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {retrying > 0
                            ? `Retrying… (attempt ${retrying + 1}/4)`
                            : submitting
                                ? "Submitting…"
                                : "Request assessment"}
                    </button>
                </div>

                {/* Persistent "trouble submitting?" escape hatch.
                    This is the ONLY thing in the entire flow that gets us
                    from ~98% capture → ~100%. If literally everything else
                    fails (network dead, server down, browser crash, JS
                    blocked) the parent still has a number to call. Always
                    visible — bottom of the form. */}
                <div
                    data-testid="assessment-contact-fallback"
                    className="mt-4 border-t border-zinc-800 pt-4 text-center"
                >
                    <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">
                        Trouble submitting? Reach us directly:
                    </p>
                    <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-4 items-center justify-center text-sm">
                        <a
                            href="tel:+14256414310"
                            data-testid="assessment-contact-phone"
                            className="text-white hover:text-brand-red transition-colors font-bold inline-flex items-center gap-1.5"
                        >
                            <Phone className="w-4 h-4 text-brand-red" /> (425) 641-4310
                        </a>
                        <span className="text-zinc-600 hidden sm:inline">·</span>
                        <a
                            href="mailto:sachinm@maxsc.net"
                            data-testid="assessment-contact-email"
                            className="text-white hover:text-brand-red transition-colors inline-flex items-center gap-1.5"
                        >
                            <Mail className="w-4 h-4 text-brand-red" /> sachinm@maxsc.net
                        </a>
                    </div>
                </div>
            </div>
        </Shell>
    );
}

function Shell({ onClose, children }) {
    return (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4">
                <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-xl my-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
                    <div className="flex justify-end px-4 pt-4 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
                        <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white" data-testid="assessment-modal-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="px-6 pb-6">{children}</div>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children, error }) => (
    <label className="block">
        <span className={`text-[11px] tracking-[0.2em] uppercase ${error ? "text-brand-red" : "text-zinc-300"}`}>{label}</span>
        <div className="mt-1.5">{children}</div>
        {error && (
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-brand-red">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{error}</span>
            </div>
        )}
    </label>
);

function Input({ value, onChange, type = "text", testid, placeholder, hasError, inputRef, onFocus }) {
    return (
        <input
            ref={inputRef}
            type={type}
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            placeholder={placeholder}
            className={`w-full bg-zinc-900 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none ${
                hasError
                    ? "border-brand-red focus:border-brand-red ring-1 ring-brand-red/50"
                    : "border-zinc-700 focus:border-brand-red"
            }`}
        />
    );
}
